import { useState } from 'react';
import { BedDouble, Bath, Maximize, MapPin, ArrowUpRight, Sparkles, KeyRound } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { PROPERTIES } from '../data/content';
import { Reveal, Eyebrow } from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statIcon = (value) => {
  if (value.includes('Beds')) return BedDouble;
  if (value.includes('sq ft')) return Maximize;
  if (value.includes('Suite')) return KeyRound;
  if (value.includes('Renovated')) return Sparkles;
  return Bath;
};

export const Properties = () => {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submitViewing = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        project_type: 'Ready Property Inquiry',
        property_interest: selected.title,
        message: form.message || `Private viewing requested for ${selected.title}`,
      });
      toast.success('Request received — we will be in touch shortly to arrange your private viewing.');
      setSelected(null);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="properties" data-testid="properties-section" className="bg-sand">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
        <div className="mb-16 md:mb-20">
          <Eyebrow>Ready Properties</Eyebrow>
          <Reveal>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-light leading-tight text-espresso sm:text-4xl lg:text-5xl">
              Currently ready, or just <span className="italic text-bronze">weeks away.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-stone md:text-base">
              Move-in-ready Invicto builds available now. Every home carries the same finish standard
              as our bespoke commissions.
            </p>
          </Reveal>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {PROPERTIES.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.1}>
              <article
                data-testid={`property-card-${i}`}
                className="group flex h-full flex-col border border-bronze/25 bg-white shadow-warm-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-warm"
              >
                <div className="relative h-60 overflow-hidden bg-white sm:h-72">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className={`h-full w-full transition-transform duration-[1400ms] group-hover:scale-105 ${
                      p.fit === 'contain' ? 'object-contain p-4' : 'object-cover'
                    }`}
                  />
                  <span className="absolute left-5 top-5 border border-gold/50 bg-espresso/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur">
                    {p.status}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-6 md:p-8">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-2xl font-normal text-espresso">{p.title}</h3>
                      <p className="mt-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone">
                        <MapPin size={12} className="text-bronze" /> {p.location}
                      </p>
                    </div>
                    <p className="font-serif text-2xl font-light text-bronze md:text-3xl">{p.price}</p>
                  </div>

                  <div
                    className="mt-6 grid border-y border-bronze/20"
                    style={{ gridTemplateColumns: `repeat(${p.stats.length}, minmax(0, 1fr))` }}
                  >
                    {p.stats.map((s, j) => {
                      const Icon = statIcon(s);
                      return (
                        <div
                          key={j}
                          className={`flex flex-col items-center gap-1 py-4 ${j < p.stats.length - 1 ? 'border-r border-bronze/20' : ''}`}
                        >
                          <Icon size={16} className="text-bronze" />
                          <span className="px-2 text-center text-sm text-espresso">{s}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-auto pt-6">
                    <button
                      data-testid={`property-schedule-viewing-button-${i}`}
                      onClick={() => setSelected(p)}
                      className="group/btn flex w-full items-center justify-center gap-2 border border-bronze/40 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-bronze transition-all duration-300 hover:bg-gold hover:text-espresso"
                    >
                      Request Private Viewing
                      <ArrowUpRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg border-bronze/25 bg-paper" data-testid="viewing-dialog">
          {selected && (
            <form onSubmit={submitViewing} data-testid="viewing-form" className="space-y-5">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">Private Viewing</p>
                <h3 className="mt-2 font-serif text-2xl font-light text-espresso">{selected.title}</h3>
                <p className="mt-1 text-sm text-stone">{selected.price} · {selected.stats.join(' · ')}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="v-name" className="text-coffee">Full Name</Label>
                <Input
                  id="v-name" required data-testid="viewing-input-name" placeholder="Your name"
                  className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="v-email" className="text-coffee">Email</Label>
                  <Input
                    id="v-email" type="email" required data-testid="viewing-input-email" placeholder="you@email.com"
                    className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="v-phone" className="text-coffee">Phone</Label>
                  <Input
                    id="v-phone" data-testid="viewing-input-phone" placeholder="(555) 000-0000"
                    className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="v-message" className="text-coffee">Note (optional)</Label>
                <Textarea
                  id="v-message" data-testid="viewing-input-message" rows={3}
                  placeholder={`Preferred days for a viewing of ${selected.title}`}
                  className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>
              <Button
                type="submit" disabled={submitting} data-testid="viewing-submit-button"
                className="w-full bg-gold font-mono text-[11px] uppercase tracking-[0.25em] text-espresso hover:bg-goldhover"
              >
                {submitting ? 'Sending…' : 'Request Viewing'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
